"use client";

import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Pagination,
  Switch,
  Breadcrumb,
  Typography,
} from "antd";
import { HomeOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { theme } from "antd";
import { apiRequest } from "../../../../../service/apiRequest";
import { showSuccess, showError } from "../../../../../components/errorHandler";
import { useCurrentModule } from "../../../../../hooks/useCurrentModule";
import IssueMasterModal from "./IssueMasterModal";
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { Select } from "antd";
const { Text } = Typography;

/* ---------------- TYPES ---------------- */
export interface Employee {
  employee_id: number;
  employee_lname: string;
  employee_fname: string;
  employee_mobile: string;
}
export interface Product {
  product_id: number;
  product_name: string;
  product_price?: number;
  last_purchase_price?: number;
  ut_id?: number;
}
export interface PO {
  pbill_id: number;
  pbill_bill_no: string;
}

export interface WorkOrder {
  wo_id: number;
  product_name: string;
  wo_pi_id?: number | null;
  wo_number?: string | null;
}

export interface UnitType {
  ut_id: number;
  ut_name: string;
}
export interface IssueItem {
  key: string;
  product_id?: number;
  unit_id?: number;
  qty: number;
  price: number;
  total: number;
}
export interface IssueMasterData {
  im_id: number;
  im_issue_to: string;
  im_type: string;
  im_status: number;
  employee_name: string;
  im_master_pi_no: string;
  im_master_order_no: string;
  employee_id: number;
  im_created_on: string;

  // Optional relational fields from backend
  po?: {
    pbill_bill_no: string;
  };
  work_order?: {
    work_order_no: string;
  };
  im_total?: number;
  created_by?: string;
}

/* ---------------- COMPONENT ---------------- */

const IssueMaster: React.FC = () => {
  const { employees, loaded } = useAppSelector(state => state.master);
  console.log("employees", employees)
  const [data, setData] = useState<IssueMasterData[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<IssueMasterData | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null);

  const currentModule = useCurrentModule();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  /* ---------------- FETCH DATA ---------------- */

  const fetchData = async (empId?: number) => {
    try {
      const url = empId
        ? `issue-master?type=Store&employee_id=${empId}`
        : "issue-master?type=Store";

      const res = await apiRequest<IssueMasterData[]>({
        method: "get",
        url,
      });
      setData(res || []);
    } catch {
      showError("Failed to fetch Issue Master data");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ---------------- MODAL HANDLERS ---------------- */

  const handleCreate = () => fetchData();

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const showEditModal = async (record: IssueMasterData) => {
    try {
      const response = await apiRequest<any>({
        method: "get",
        url: `issue-master?id=${record.im_id}`
      });
      if (!response) {
        showError("Failed to load issue details");
        return;
      }
      setIsEditMode(true);
      setInitialValues(response);
      setIsModalVisible(true);
    } catch {
      showError("Error loading issue details");
    }
  };

  const hideModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  /* ---------------- DELETE ---------------- */

  const handleDelete = async (id: number) => {
    try {
      await apiRequest({
        method: "delete",
        url: `issue-delete/${id}`,
      });
      showSuccess("Store issue master deleted successfully");
      fetchData();
    } catch {
      showError("Failed to delete store issue master");
    }
  };

  /* ---------------- STATUS TOGGLE ---------------- */

  const handleStatusToggle = async (id: number) => {
    // Optimistic UI update
    setData((prev) =>
      prev.map((item) =>
        item.im_id === id ? { ...item, im_status: item.im_status === 1 ? 0 : 1 } : item
      )
    );

    try {
      await apiRequest({
        method: "put",
        url: `issue-status-toggle/${id}`,
      });
      showSuccess("Status updated");
    } catch {
      showError("Failed to update status");
      fetchData(); // revert on failure
    }
  };

  /* ---------------- TABLE COLUMNS ---------------- */

  const columns = [
    {
      title: "Issue To",
      dataIndex: "employee_name",
      key: "employee_name",
      render: (val: string) => val || "-",
    },
    {
      title: "PI Number",
      key: "po",
      render: (_: any, record: IssueMasterData) =>
        record.im_master_pi_no || "-",
    },
    {
      title: "Work Order No",
      key: "work_order",
      render: (_: any, record: IssueMasterData) =>
        record.im_master_order_no || "-",
    },
    {
      title: "Grand Total",
      dataIndex: "im_total",
      key: "im_total",
      render: (val?: number) => (
        <Text strong>{val ? val.toFixed(4) : "0.0000"}</Text>
      ),
    },
    {
      title: "Issue By",
      dataIndex: "created_by",
      key: "created_by",
      render: (created_by?: string) => (
        <Text strong>{created_by || "-"}</Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "im_status",
      key: "im_status",
      render: (_: any, record: IssueMasterData) => (
        <Switch
          checked={record.im_status === 1}
          onChange={() => handleStatusToggle(record.im_id)}
        />
      ),
    },
  ];

  /* ---------------- ACTION COLUMN ---------------- */

  if (currentModule?.right_edit === "Y" || currentModule?.right_delete === "Y") {
    columns.push({
      title: "Action",
      key: "action",
      dataIndex: "im_id", // ✅ REQUIRED for TS
      render: (_, record) => (
        <Space>
          <Button onClick={() => showEditModal(record)}>
            <EditOutlined />
          </Button>

          <Popconfirm
            title="Delete?"
            onConfirm={() => handleDelete(record.im_id)}
          >
            <Button danger>
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </Space>
      ),
    });
  }

  /* ---------------- RENDER ---------------- */

  return (
    <>
      <Space
        align="center"
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Breadcrumb>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Issue Master - Store</Breadcrumb.Item>
        </Breadcrumb>

        <Space align="center">
          <Select
            style={{ width: 250 }}
            showSearch
            optionFilterProp="label"
            allowClear
            placeholder="Filter by Employee"
            value={selectedEmployee}
            onChange={(value) => {
              setSelectedEmployee(value);
              fetchData(value);
            }}
          >
            {employees.map((emp) => (
              <Select.Option key={emp.employee_id} value={emp.employee_id}
                label={`${emp.employee_fname} ${emp.employee_lname}`}>
                {emp.employee_fname} {emp.employee_lname}
              </Select.Option>
            ))}
          </Select>
        </Space>

        {currentModule?.right_add === "Y" && (
          <Button type="primary" onClick={showCreateModal}>
            Add New Issue Master - Store
          </Button>
        )}
      </Space>


      <div style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}>
        <Table<IssueMasterData>
          columns={columns}
          dataSource={data.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          rowKey="im_id"
          pagination={false}
          scroll={{ y: 500 }}
          footer={() => (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, data.length)} of{" "}
                {data.length}
              </Text>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={data.length}
                showSizeChanger
                onChange={(page, pageSize) =>
                  setPagination({ current: page, pageSize })
                }
              />
            </div>
          )}
        />
      </div>

      <IssueMasterModal
        visible={isModalVisible}
        onCancel={hideModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues || undefined}
      />
    </>
  );
};

export default IssueMaster;